interface ExpectedQueryParams {
   utm_source?: string;
   utm_medium?: string;
   utm_campaign?: string;
   utm_content?: string;
   // Sometimes a user will register and should at that point be linked to a particular site
   study_site_code?: string;
   // Some Portals require a registration code (which is unique to each user) to exist before a user can register
   registration_code?: string;
   // Some Portals require an 'invite reg' code (which is not user-specific) to exist before a user can register
   invitation_code?: string;
}

/** Get all query parameters and convert to an object */
function getQueryParamsAsObject(): ExpectedQueryParams {
   if (import.meta.env.SSR) return {}; // cant use window in SSR

   const params = new URLSearchParams(window.location.search);
   const obj: ExpectedQueryParams = {};
   for (const [key, value] of params) {
      // change r to registrationCode
      if (key === "r") {
         obj["registration_code"] = value;
      } else if (key === "i") {
         obj["invitation_code"] = value;
      } else if (key === "site") {
         obj["study_site_code"] = value;
      }
      // if there are any other unknowns, just add them to the object
      else {
         obj[key] = value;
      }
   }
   return obj;
}

/** Add a query parameter to the current URL (or replace if it already exists)
 * @param targetParam target query param to update
 * @param value new query param value
 * @param url optional URL to update (if not provided, the current URL will be used)
 * @returns updated query string
 */
function updateQueryParam(targetParam: string, value: string, url: string = window.location.href): string {
   // Create a URL object using the provided URL or the current location
   const urlObject: URL = new URL(url);

   // Use the searchParams API to set or update the parameter
   urlObject.searchParams.set(targetParam, value);

   // Return the full URL as a string
   return urlObject.toString();
}

/** Redirect to the new locale while keeping the rest of the URL in tact such as query params
 * @param routeParamLocale ex. 'en-gb'
 * @param newLocale ex 'en-us'
 */
function replaceLocaleInUrl(routeParamLocale: string | string[], newLocale: string, url?: string): string {
   // Use the provided URL or default to the current window location
   const currentUrl = url || window.location.href;
   const currentOrigin = url ? new URL(url).origin : window.location.origin;
   const currentPathname = url ? new URL(url).pathname : window.location.pathname;
   const currentSearch = url ? new URL(url).search : window.location.search;

   let newUrl = "";

   // If there is no locale in the URL, add it to the beginning (keep query params intact)
   if (!routeParamLocale) {
      newUrl = `${currentOrigin}/${newLocale}${currentPathname}`;

      // append query params if they exist
      if (currentSearch) {
         newUrl += currentSearch;
      }
   }
   // Replace an existing locale in the URL with the new one (query params will remain intact)
   else {
      // Ensure the new locale is always in the correct format with a leading slash,
      // but without an additional trailing slash, to avoid double slashes.
      const formattedNewLocale = `/${newLocale}`;

      // Use a regular expression to find and replace the old locale.
      // - The pattern `/${routeParamLocale}(?=/|\\?|#|$)` matches the old locale only if it is followed
      //   by a slash (/), a query string (?), a hash (#), or the end of the URL ($). This ensures that we
      //   replace the locale properly even when followed by a query parameter or fragment.
      // - We replace the old locale with the new formatted locale.
      newUrl = currentUrl.replace(new RegExp(`/${routeParamLocale}(?=/|\\?|#|$)`), formattedNewLocale);
   }

   return newUrl;
}

export { getQueryParamsAsObject, replaceLocaleInUrl, updateQueryParam };
